// src/styles/globalStyles.ts
import { createGlobalStyle } from 'styled-components';
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/500.css';
import '@fontsource/playfair-display/600.css';
import '@fontsource/playfair-display/700.css';
import '@fontsource/raleway/300.css';
import '@fontsource/raleway/400.css';
import '@fontsource/raleway/500.css';
import '@fontsource/raleway/600.css';
import '@fontsource/raleway/700.css';
import '@fontsource/cormorant-garamond/400.css';
import '@fontsource/cormorant-garamond/500.css';
import '@fontsource/cormorant-garamond/600.css';

export const GlobalStyles = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  html {
    scroll-behavior: smooth;
    font-size: 16px;
  }

  body {
    font-family: ${({ theme }) => theme.fonts.body};
    color: ${({ theme }) => theme.colors.dark};
    background-color: ${({ theme }) => theme.colors.light};
    line-height: 1.5;
    overflow-x: hidden;
    will-change: opacity;
    transition: background-color 0.2s ease;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    margin-bottom: ${({ theme }) => theme.space.md};
    line-height: ${({ theme }) => theme.lineHeights.tight};
    letter-spacing: -0.01em;
  }

  h1 {
    font-size: ${({ theme }) => theme.fontSizes['5xl']};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes['4xl']};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes['3xl']};
    }
  }

  h2 {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes['3xl']};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes['2xl']};
    }
  }

  h3 {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes['2xl']};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes.xl};
    }
  }

  h4 {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.xl};
    }
  }

  h5 {
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }

  h6 {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }

  p {
    margin-bottom: ${({ theme }) => theme.space.md};
    font-size: ${({ theme }) => theme.fontSizes.md};
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    transition: color 0.2s ease;
  }

  button {
    font-family: ${({ theme }) => theme.fonts.body};
    cursor: pointer;
    border: none;
    outline: none;
  }

  img, video {
    max-width: 100%;
    height: auto;
    display: block;
    will-change: transform;
  }

  ul, ol {
    list-style-position: inside;
    margin-bottom: ${({ theme }) => theme.space.md};
  }

  section {
    padding: ${({ theme }) => theme.space['3xl']} 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      padding: ${({ theme }) => theme.space['2xl']} 0;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding: ${({ theme }) => theme.space.xl} 0;
    }
  }

  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 ${({ theme }) => theme.space.md};
    position: relative;
    z-index: 1;
  }

  .section-title {
    margin-bottom: ${({ theme }) => theme.space.xl};
    text-align: center;
    position: relative;
    font-family: ${({ theme }) => theme.fonts.heading};

    &::after {
      content: '';
      position: absolute;
      bottom: -${({ theme }) => theme.space.sm};
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 3px;
      background: ${({ theme }) => theme.colors.accent};
      border-radius: ${({ theme }) => theme.borderRadius.full};
    }
  }

  .text-gradient {
    background: ${({ theme }) => theme.colors.gradient.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  }
  
  .accent-text {
    font-family: ${({ theme }) => theme.fonts.accent};
    color: ${({ theme }) => theme.colors.accent};
    font-style: italic;
  }
  
  .elegant-shadow {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  }
  
  .border-accent {
    border-left: 3px solid ${({ theme }) => theme.colors.accent};
    padding-left: ${({ theme }) => theme.space.md};
  }

  /* Performance optimizations */
  .page-ready * {
    transition-delay: 0ms !important;
  }

  /* Optimize content loading */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Optimize motion for users who prefer reduced motion */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Optimize layout shifts */
  html {
    scroll-behavior: smooth;
  }

  /* Optimize for mobile */
  @media (max-width: 768px) {
    html {
      font-size: 14px;
    }
  }
`;

export default GlobalStyles;
